// H12 — generic service test-panel renderer.
//
// The gateway derives a DECLARATIVE panel per service (auto from each tool's MCP input JSON-schema +
// the service-shipped runtime_config.test_panel override). This component renders ANY such panel with
// NO per-service code: it builds a form from the field descriptors, runs the selected tool through the
// existing /tools/call path (reused from the tools tester), and renders the result with a media preview
// (image / audio / video / text / json). Unknown field/preview types degrade gracefully.

import { useMemo, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useResource } from '../lib/useResource';
import { useAuth } from '../auth/AuthContext';
import { Drawer } from '../components/Drawer';
import { StreamViewer } from '../components/StreamViewer';
import { Badge, ErrorState, JsonView, Loading, bytesHuman } from '../components/ui';
import type {
  ServiceCatalogEntry,
  TestPanel as TestPanelT,
  TestPanelField as Field,
  TestPanelResult as ResultHint,
  TestPanelTool,
  ToolCallResult,
} from '../api/schemas';

const KNOWN_FIELD_TYPES = new Set(['text', 'number', 'bool', 'enum', 'ref', 'json']);

// --- argument assembly ----------------------------------------------------
type Values = Record<string, unknown>;

function defaultValues(tool: TestPanelTool): Values {
  const v: Values = {};
  for (const f of tool.fields) {
    if (f.default !== undefined && f.default !== null) v[f.name] = f.default;
    else if (f.type === 'bool') v[f.name] = false;
    else v[f.name] = '';
  }
  return v;
}

// Build the JSON arguments object from the raw form values: coerce numbers, parse json fields, and
// drop empty optional fields so the tool sees only what was actually supplied.
export function buildArgs(tool: TestPanelTool, values: Values): { args: Values; error?: string } {
  const args: Values = {};
  for (const f of tool.fields) {
    const raw = values[f.name];
    const empty = raw === '' || raw === undefined || raw === null;
    if (empty) {
      if (f.required) return { args, error: `“${f.label || f.name}” is required.` };
      continue;
    }
    if (f.type === 'number') {
      const n = Number(raw);
      if (Number.isNaN(n)) return { args, error: `“${f.label || f.name}” must be a number.` };
      args[f.name] = n;
    } else if (f.type === 'bool') {
      args[f.name] = Boolean(raw);
    } else if (f.type === 'json') {
      try {
        args[f.name] = typeof raw === 'string' ? JSON.parse(raw) : raw;
      } catch {
        return { args, error: `“${f.label || f.name}” is not valid JSON.` };
      }
    } else {
      args[f.name] = raw;
    }
  }
  return { args };
}

// --- field controls -------------------------------------------------------
function FieldControl({
  field,
  value,
  onChange,
}: {
  field: Field;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const type = KNOWN_FIELD_TYPES.has(field.type) ? field.type : 'unknown';
  const label = (
    <span>
      {field.label || field.name}
      {field.required && <span className="req"> *</span>}
      {type === 'unknown' && <span className="muted small"> (raw)</span>}
    </span>
  );
  let control;
  if (type === 'bool') {
    control = (
      <input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
    );
  } else if (type === 'enum') {
    control = (
      <select value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
        <option value="">— choose —</option>
        {field.options.map((o) => (
          <option key={String(o)} value={String(o)}>{String(o)}</option>
        ))}
      </select>
    );
  } else if (type === 'number') {
    control = (
      <input
        type="number"
        value={value === undefined || value === null ? '' : String(value)}
        min={field.minimum}
        max={field.maximum}
        step={typeof field.step === 'number' ? field.step : undefined}
        placeholder={field.placeholder ?? undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  } else if (field.multiline || type === 'json') {
    control = (
      <textarea
        className="code-input"
        rows={type === 'json' ? 4 : 3}
        value={typeof value === 'string' ? value : JSON.stringify(value ?? '')}
        placeholder={field.placeholder ?? (type === 'json' ? '{ }' : undefined)}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  } else {
    // text, ref, and unknown types fall back to a single-line input (degrade gracefully).
    control = (
      <input
        type="text"
        value={typeof value === 'string' ? value : String(value ?? '')}
        placeholder={field.placeholder ?? (type === 'ref' ? 'object ref / key…' : undefined)}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <label className="field panel-field">
      {label}
      {control}
      {field.description && <span className="muted small">{field.description}</span>}
      {type === 'ref' && <span className="muted small">paste an object ref / key produced by a prior call</span>}
    </label>
  );
}

// --- result + media preview ----------------------------------------------
function refToUrl(ref: string): string {
  // A full URL (http(s)/data) is used verbatim; otherwise treat it as a network-scoped object key/ref
  // and resolve it through the gateway's object download (302 → presigned URL).
  if (/^(https?:|data:|blob:)/i.test(ref)) return ref;
  return api.objectDownloadUrl(ref);
}

// Pull the artifact ref/key/url out of a tool output using the panel's ref_path hint, with a best-effort
// fallback across the conventional keys MCPGRAPH services use.
export function extractRef(output: unknown, hint?: ResultHint): string | undefined {
  if (typeof output === 'string') return output;
  if (!output || typeof output !== 'object') return undefined;
  const path = hint?.ref_path;
  const keys = ['ref', 'url', 'key', 's3_key', 'image_ref', 'audio_ref', 'video_ref', 'output'];
  // Search the output object directly, then — for MCP results — the JSON parsed from its text content
  // (MCPGRAPH services return artifacts as a JSON body like {"ref": "...", "width": ...}).
  const candidates: Record<string, unknown>[] = [output as Record<string, unknown>];
  const text = extractText(output);
  if (text) {
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object') candidates.push(parsed as Record<string, unknown>);
    } catch {
      /* not JSON — ignore */
    }
  }
  for (const o of candidates) {
    if (path && typeof o[path] === 'string') return o[path] as string;
    for (const k of keys) if (typeof o[k] === 'string') return o[k] as string;
  }
  return undefined;
}

// Extract a plain-text body from an MCP-style result (sync tools commonly return {content:[{text}]}).
export function extractText(output: unknown): string | undefined {
  if (typeof output === 'string') return output;
  if (output && typeof output === 'object') {
    const o = output as Record<string, unknown>;
    if (Array.isArray(o.content)) {
      const parts = o.content
        .map((c) => (c && typeof c === 'object' && 'text' in c ? String((c as { text: unknown }).text) : ''))
        .filter(Boolean);
      if (parts.length) return parts.join('\n');
    }
    if (typeof o.text === 'string') return o.text;
  }
  return undefined;
}

export function MediaPreview({ output, hint }: { output: unknown; hint?: ResultHint }) {
  const type = hint?.type ?? 'auto';
  const ref = extractRef(output, hint);

  if (type === 'image' || type === 'audio' || type === 'video') {
    if (!ref) return <div className="muted small">No artifact ref in the result to preview.</div>;
    const url = refToUrl(ref);
    return (
      <div className="media-preview" data-preview={type}>
        {type === 'image' && <img className="preview-img" src={url} alt="generated artifact" />}
        {type === 'audio' && <audio className="preview-audio" controls src={url} />}
        {type === 'video' && <video className="preview-video" controls src={url} />}
        <div className="muted small">
          <a href={url} target="_blank" rel="noreferrer">open artifact</a> · <code className="mono">{ref}</code>
        </div>
      </div>
    );
  }
  if (type === 'text') {
    const text = extractText(output) ?? (typeof output === 'string' ? output : JSON.stringify(output, null, 2));
    return <pre className="json-view result-text">{text}</pre>;
  }
  if (type === 'json') return <JsonView value={output} />;

  // auto / unknown: prefer a text body if present; otherwise show the JSON (degrade gracefully).
  const text = extractText(output);
  if (text) return <pre className="json-view result-text">{text}</pre>;
  return <JsonView value={output} />;
}

function ResultView({ result, hint }: { result: ToolCallResult; hint?: ResultHint }) {
  if (result.kind === 'result') {
    return (
      <div>
        <Badge tone="ok">result</Badge>
        {result.usage && (
          <div className="muted small">
            gpu_seconds {result.usage.gpu_seconds} · {bytesHuman(result.usage.bytes)} · {result.usage.tokens} tokens
          </div>
        )}
        <MediaPreview output={result.output} hint={hint} />
      </div>
    );
  }
  if (result.kind === 'task') {
    return (
      <div>
        <Badge tone="warn">async task</Badge>
        <div className="muted small">queued as <code className="mono">{result.task_id}</code> ({result.state}). Poll it under Overview → Activity → Tasks.</div>
      </div>
    );
  }
  // ST41 — a streaming tool returns a descriptor with a ready-to-consume ws_url
  // (carries its single-use consumer token). Render the live content inline.
  return (
    <div>
      <Badge tone="info">stream</Badge>
      <div className="muted small">
        streaming tool — <code className="mono small">{result.stream_id}</code>
        {result.media_type ? ` · ${result.media_type}` : ''}
      </div>
      <StreamViewer wsUrl={result.ws_url} mediaType={result.media_type} />
    </div>
  );
}

// --- per-tool runner ------------------------------------------------------
function ToolRunner({ tool, token }: { tool: TestPanelTool; token: string }) {
  const [values, setValues] = useState<Values>(() => defaultValues(tool));
  const [calling, setCalling] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<ToolCallResult | null>(null);

  const runnable = Boolean(tool.canonical_name) && tool.live_providers > 0;

  async function run() {
    if (!tool.canonical_name) return;
    const { args, error } = buildArgs(tool, values);
    if (error) {
      setErr(error);
      return;
    }
    setCalling(true);
    setErr(null);
    setResult(null);
    try {
      const r = await api.callTool({ canonical_tool: tool.canonical_name, arguments: args, token });
      setResult(r);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setCalling(false);
    }
  }

  return (
    <div className="panel-tool">
      <div className="panel-tool-head">
        <h4>{tool.title || tool.tool}</h4>
        <div className="muted-row">
          <code className="mono small">{tool.tool}</code>
          {tool.streaming && <Badge tone="info">stream</Badge>}
          {tool.canonical_name ? (
            tool.live_providers > 0 ? (
              <Badge tone="ok">{tool.live_providers} live provider{tool.live_providers === 1 ? '' : 's'}</Badge>
            ) : (
              <Badge tone="muted">no live provider</Badge>
            )
          ) : (
            <Badge tone="muted">not published</Badge>
          )}
        </div>
      </div>
      {tool.description && <p className="muted small">{tool.description}</p>}

      {tool.fields.length === 0 && (
        <p className="muted small">No input fields declared — this tool takes no arguments (or its schema is not yet known).</p>
      )}
      <div className="panel-fields">
        {tool.fields.map((f) => (
          <FieldControl
            key={f.name}
            field={f}
            value={values[f.name]}
            onChange={(v) => setValues((prev) => ({ ...prev, [f.name]: v }))}
          />
        ))}
      </div>

      {err && <ErrorState message={err} />}
      <div className="muted-row">
        <button className="btn btn-primary btn-sm" disabled={!runnable || calling} onClick={run} title={runnable ? '' : 'Needs a published, live provider to run'}>
          {calling ? 'Running…' : 'Run'}
        </button>
        {!tool.canonical_name && <span className="muted small">Publish this service to a network you can reach to run it.</span>}
        {tool.canonical_name && tool.live_providers === 0 && <span className="muted small">Assign the service to a node so a provider is live.</span>}
      </div>

      {result && (
        <div className="panel-result">
          <h5>Result</h5>
          <ResultView result={result} hint={tool.result} />
        </div>
      )}
    </div>
  );
}

// --- artifact ref preview (media-preview helper) --------------------------
// Render any known object ref/key through the same MediaPreview pipeline — handy for previewing a prior
// generation's artifact and for verifying the media renderer against a known object-store artifact.
function ArtifactPreview() {
  const [ref, setRef] = useState('');
  const [kind, setKind] = useState<'image' | 'audio' | 'video'>('image');
  const [shown, setShown] = useState<{ ref: string; kind: string } | null>(null);
  return (
    <div className="panel-artifact">
      <h5>Preview an artifact by ref / key</h5>
      <div className="muted-row artifact-row">
        <select value={kind} onChange={(e) => setKind(e.target.value as 'image' | 'audio' | 'video')}>
          <option value="image">image</option>
          <option value="audio">audio</option>
          <option value="video">video</option>
        </select>
        <input
          className="artifact-ref"
          type="text"
          placeholder="object ref / network-scoped key…"
          value={ref}
          onChange={(e) => setRef(e.target.value)}
        />
        <button className="btn btn-sm" disabled={!ref} onClick={() => setShown({ ref, kind })}>Preview</button>
      </div>
      {shown && <MediaPreview output={{ ref: shown.ref }} hint={{ type: shown.kind }} />}
    </div>
  );
}

// --- test token minting ---------------------------------------------------
// Build the issueMcpToken body for a "run as me" test token: broad tool access (allow-all selector)
// across the caller's accessible networks, with the scopes /tools/call needs. This mints a REAL,
// usable mcp-access token — unlike a grant's `token_prefix`, which is a non-secret display string the
// gateway rejects with 400 "provided token is not active".
export function buildMcpTokenBody(userId: string, networkIds: string[]) {
  return {
    user_id: userId,
    scopes: ['mcp:invoke', 'mcp:list'],
    tool_selectors: { allow: ['*__*__*'], deny: [] as string[] },
    network_ids: networkIds,
  };
}

// --- drawer ---------------------------------------------------------------
export function TestPanelDrawer({ entry, onClose }: { entry: ServiceCatalogEntry; onClose: () => void }) {
  const panel = useResource<TestPanelT>(() => api.testPanel(entry.id), [entry.id]);
  const { user } = useAuth();
  const [token, setToken] = useState('');
  const [minting, setMinting] = useState(false);
  const [mintErr, setMintErr] = useState<string | null>(null);
  const [minted, setMinted] = useState(false);

  async function generateToken() {
    if (!user?.id) {
      setMintErr('No signed-in user to run as — paste an mcp-access token instead.');
      return;
    }
    setMinting(true);
    setMintErr(null);
    setMinted(false);
    try {
      // Run as the CURRENT user across every network they can reach (empty = none; the gateway
      // scopes networks() to the caller). A freshly minted token is active immediately.
      const nets = await api.networks().catch(() => ({ items: [] as { id: string }[] }));
      const issued = await api.issueMcpToken(buildMcpTokenBody(user.id, nets.items.map((n) => n.id)));
      setToken(issued.token);
      setMinted(true);
    } catch (e) {
      setMintErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setMinting(false);
    }
  }

  const subtitle = useMemo(
    () => (
      <span>
        <code className="mono small">{entry.image_ref}</code>
        {panel.data && <> · panel source: <Badge tone={panel.data.source === 'auto' ? 'muted' : 'info'}>{panel.data.source}</Badge></>}
      </span>
    ),
    [entry.image_ref, panel.data],
  );

  return (
    <Drawer title={`Test · ${entry.name}`} subtitle={subtitle} onClose={onClose} width={620}>
      {panel.loading && <Loading what="Loading test panel…" />}
      {panel.error && <ErrorState message={panel.error} onRetry={panel.reload} />}
      {panel.data && (
        <div className="test-panel">
          {panel.data.title && <p className="muted">{panel.data.title}</p>}

          <div className="field">
            <span>mcp-access token (to run as {user?.email ?? 'current user'})</span>
            <div className="muted-row">
              <button className="btn btn-sm" disabled={minting} onClick={generateToken}>
                {minting ? 'Generating…' : 'Generate test token'}
              </button>
              {minted && token && <Badge tone="ok">token ready</Badge>}
            </div>
            <span className="muted small">
              Mints a fresh mcp-access token for you (allow-all across your networks) and runs with it.
            </span>
          </div>
          {mintErr && <ErrorState message={mintErr} />}
          <input
            className="field-input"
            placeholder="…or paste an mcp-access token"
            value={token}
            onChange={(e) => {
              setToken(e.target.value);
              setMinted(false);
            }}
          />

          {panel.data.tools.length === 0 && <p className="muted small">This service exposes no tools.</p>}
          {panel.data.tools.map((t) => (
            <ToolRunner key={t.tool} tool={t} token={token} />
          ))}

          <ArtifactPreview />
        </div>
      )}
    </Drawer>
  );
}
